<template>
  <div v-if="!auxExists">
    <template v-if="mainControlType === 'fileuploadsingle'">
      <template v-if="data[col.ColumnNamePc]">
        <a
          class="text-[14px] flex flex-row cursor-pointer justify-start gap-2 hover:underline"
          :href="data._pretty[col.ColumnNamePc]"
          target="_blank"
        >
          <i class="pi pi-download" />
          Download
        </a>
      </template>
      <template v-else>
        <div class="italic text-[14px]">None</div>
      </template>
    </template>
    <template v-else-if="isDateLike">
      <div class="text-[14px]">
        {{
          data[col.ColumnNamePc] ? changeDateFormat(data[col.ColumnNamePc]) : ""
        }}
      </div>
    </template>
    <template v-else-if="mainControlType === 'datetime'">
      <div class="text-[14px]">
        {{
          data[col.ColumnNamePc]
            ? changeDateTimeFormat(data[col.ColumnNamePc])
            : ""
        }}
      </div>
    </template>
    <template v-else-if="mainControlType === 'html'">
      <div class="flex flex-row items-center gap-1">
        <nuxt-link
          :to="`/table/${props.meta.table.TableName}/${data.Id}`"
          class="text-[14px] text-[#085394] font-medium whitespace-nowrap"
        >
          {{ data[col.ColumnNamePc] }}</nuxt-link
        >
        <i class="pi pi-chevron-right text-[#085394] text-[10px]" />
      </div>
    </template>
    <template v-else-if="mainControlType === 'percent'">
      <div class="text-[14px]">
        {{ Number(data[col.ColumnNamePc]).toFixed(2).replace(/\.00$/, "") }}%
      </div>
    </template>
    <template v-else-if="mainControlType === 'currency'">
      <div class="text-[14px]">
        <UiTruncatedValue
          :value="`${String(
            Number(data[col.ColumnNamePc] ?? 0)
              .toFixed(2)
              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
          )}`"
          :max-width="'400px'"
        />
      </div>
    </template>
    <template v-else-if="mainControlType === 'checkbox'">
      <div class="flex flex-row justify-start w-full pl-3">
        <template v-if="data[col.ColumnNamePc]">
          <i class="pi pi-check-circle"></i>
        </template>
        <template v-else>
          <i class="pi pi-circle"></i>
        </template>
      </div>
    </template>
    <template v-else-if="col.RouteTo && col.RouteTo != ''">
      <div class="flex flex-row items-center gap-1">
        <NuxtLink
          class="text-[14px] text-[#085394] font-medium whitespace-nowrap hover:underline"
          :to="processSlugs(col.RouteTo, data)"
        >
          <!-- {{ data }} -------- {{ col }} -->
          {{ getFormattedJoinData(data, col) }}
        </NuxtLink>
        <i class="pi pi-chevron-right text-[#085394] text-[10px]" />
      </div>
    </template>
    <template v-else-if="mainControlType === 'month'">
      <div class="text-[14px]">
        {{
          props.data[props.col.ColumnNamePc]
            ? new Date(
                2000,
                props.data[props.col.ColumnNamePc] - 1,
                1
              ).toLocaleString("default", { month: "long" })
            : ""
        }}
      </div>
    </template>
    <template v-else>
      <UiTruncatedValue :value="formattedJoinData" :max-width="'400px'" />
    </template>
  </div>
  <div v-else>
    <component :is="auxGet" :col="col" :data="data" :meta="meta"></component>
  </div>
</template>

<script setup lang="ts">
import dayjs from "dayjs";
import type { IMetaColumn, IMetaComputed } from "~/scad-shared/types/scad";

const props = defineProps<{
  col: IMetaColumn;
  data: any;
  meta: IMetaComputed;
}>();

const { getFormattedJoinData } = useFormattedJoinData();

const formattedJoinData = computed(() => {
  return getFormattedJoinData(props.data, props.col);
});

function changeDateFormat(date: string) {
  return dayjs(date).format(
    columnDateFormat(props.col.DataType, props.col.DateFormat)
  );
}

function changeDateTimeFormat(date: string) {
  return dayjs(date).format(
    props.col.DateFormat || DEFAULT_DATETIME_FORMAT
  );
}

const controlType = computed(() => {
  return props.col.ControlType?.toLowerCase() ?? "text";
});

const mainControlType = computed(() => {
  return controlType.value.split(":")[0];
});

// A daterange column holds one date per row - the range is how it is FILTERED, not what
// it contains - so it renders like any other date. Without this it falls through to text
// and shows the raw ISO value.
const isDateLike = computed(() =>
  ["date", "daterange"].includes(mainControlType.value)
);

const auxControlType = computed(() => {
  const segments = controlType.value.split(":");
  return segments.length > 1 ? segments[1].trim() : "";
});

const { exists: auxExists, get: auxGet } = useAuxInput(
  auxControlType.value,
  "gridCell"
);
</script>

<style scoped></style>
